import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Target, Compass, BookOpen, Briefcase, Brain, Layers, Users, Lightbulb,
  TrendingUp, Rocket, Shield, Monitor, ArrowRight, CheckCircle2,
} from "lucide-react";
import { stats, pillars, methods, globalSchools } from "@/data";

const iconMap: Record<string, React.ElementType> = {
  Target, Compass, BookOpen, Briefcase, Brain, Layers, Users, Lightbulb,
  TrendingUp, Rocket, Shield, Monitor,
  Repeat: TrendingUp, Search: Target, Sparkles: Lightbulb, BarChart2: TrendingUp, Globe: Monitor,
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-primary py-20 px-4">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg,transparent,transparent 40px,rgba(255,255,255,.15) 40px,rgba(255,255,255,.15) 41px),repeating-linear-gradient(90deg,transparent,transparent 40px,rgba(255,255,255,.15) 40px,rgba(255,255,255,.15) 41px)",
          }}
        />
        <div className="container mx-auto relative text-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex justify-center flex-wrap gap-2 mb-6">
              {["AACSB Aligned", "EFMD Aligned", "EQUIS Aligned", "NEP 2020", "OBE Framework"].map((b) => (
                <span key={b} className="text-xs font-semibold bg-white/15 text-white border border-white/30 px-3 py-1 rounded-full">
                  {b}
                </span>
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Pedagogical Framework
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-2 font-medium">
              Global Business School &amp; Research Centre
            </p>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              A Global, Industry-Integrated, and AI-Enabled Learning Ecosystem — aligned with the world's best management education standards.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/pillars"
                className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-lg hover:bg-white/90 transition-colors"
              >
                Explore 8 Pillars <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/benchmarks"
                className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition-colors"
              >
                Global Benchmarks
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-muted/50 border-b border-border">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="text-center"
              >
                <p className="text-3xl font-bold text-primary">{s.number}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-center mb-8">
              <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Pedagogical Vision</p>
              <h2 className="text-3xl font-bold text-foreground">Management Education as Transformation</h2>
            </div>
            <div className="bg-primary/5 border-l-4 border-primary rounded-r-xl p-6 mb-8">
              <p className="text-base text-foreground/80 leading-relaxed">
                GBSRC views management education as a <strong>transformational process</strong> — one that shapes identity, capability, and conscience, not merely credentials. The institution consciously moves beyond conventional lecture-based education toward capability development, experiential engagement, digital readiness, ethical leadership, and lifelong learning.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Knowledge is applied rather than memorised — classrooms are laboratories",
                "Learning is collaborative — every student is both a learner and a co-teacher",
                "Technology and AI augment — not replace — human judgment",
                "Industry participation is structurally embedded, not an add-on",
                "Innovation and entrepreneurship are cultivated as cultural dispositions",
                "Ethical decision-making and leadership development are continuous",
                "Lifelong learning orientation is built from day one",
              ].map((pt) => (
                <div key={pt} className="flex items-start gap-3 p-3 bg-muted/40 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground/80">{pt}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS GRID */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Core Architecture</p>
            <h2 className="text-3xl font-bold text-foreground">8 Core Pedagogical Pillars</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
              Each pillar represents a distinct dimension of GBSRC's learning philosophy.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {pillars.map((p, i) => {
              const Icon = iconMap[p.icon] ?? Target;
              return (
                <motion.div
                  key={p.id}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  className="group bg-background border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-lg transition-all duration-300 flex flex-col"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${p.color} flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-xs text-muted-foreground font-medium mb-1">{p.subtitle}</p>
                  <h3 className="font-bold text-sm text-foreground mb-2">{p.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">{p.description}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/pillars"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
            >
              Explore all pillars in detail <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GLOBAL SCHOOLS */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">World-Class Alignment</p>
            <h2 className="text-3xl font-bold text-foreground">Benchmarked Against Global Leaders</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {globalSchools.map((s, i) => (
              <motion.div
                key={s.school}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-primary text-white rounded-xl p-5"
              >
                <p className="font-bold text-sm mb-1">{s.school}</p>
                <p className="text-white/70 text-xs">{s.focus}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/benchmarks" className="inline-flex items-center gap-2 text-primary font-semibold hover:underline">
              See full benchmark table <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TEACHING METHODS STRIP */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">In the Classroom</p>
            <h2 className="text-3xl font-bold text-foreground">5 Innovative Teaching Methods</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {methods.map((m, i) => (
              <motion.div
                key={m.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="bg-background border border-border rounded-xl p-4 text-center"
              >
                <p className="font-bold text-sm text-primary mb-2">{m.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{m.description.slice(0, 90)}…</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONCLUSION */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Towards a World-Class Learning Ecosystem</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-4">
            GBSRC's pedagogical framework is <strong>not aspirational — it is operational</strong>. By integrating global best practices — from Harvard's case method to Stanford's design thinking, MIT's technology integration to Wharton's analytics culture — with India's NEP 2020 priorities, GBSRC builds management professionals who are simultaneously rooted and global.
          </p>
          <p className="text-base text-muted-foreground leading-relaxed">
            Graduates emerge not merely with a degree but with a <strong>professional identity, a problem-solving mindset, and the values</strong> needed to lead responsibly in an increasingly complex and interconnected world.
          </p>
        </div>
      </section>
    </div>
  );
}
